import React, { useState } from 'react'
import { Loader } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../../app/hooks'
import { useLoadPriceMaximizerData } from '../../../../../common/display/priceMaximizer/useLoadPriceMaximizerData'
import { InitState } from '../../../../../../redux/reducers/root/initialState'
import { selectHasRequiredAddressForValuation } from '../../../../../../redux/selectors/insertionSelectors'
import { useIsRecommendedPriceEnabled } from '../../../UseIsRecommendedPriceEnabled'
import { MarketRangeCtaNotification } from './market-range-cta-notification/MarketRangeCtaNotification'
import { MissingRequiredDataNotification } from './missing-required-data-notification/MissingRequiredDataNotification'
import {
  RecommendedPriceNotificationModal,
  RecommendedPriceNotificationModalVariant,
} from './modal/RecommendedPriceNotificationModal'
import { useLoadRentValuationData } from './useLoadRentValuationData'

export function RecommendedPriceNotification() {
  const { t } = useTranslation()
  const isRecommendedPriceEnabled = useIsRecommendedPriceEnabled()
  const [modalVariant, setModalVariant] = useState<RecommendedPriceNotificationModalVariant | null>(null)
  const { minValue, maxValue, isPending } = useAppSelector((state: InitState) => state.valuationFeature)
  const insertionData = useAppSelector((state: InitState) => state.insertionFeature.insertionData)
  const { price } = insertionData
  const hasRequiredAddressData = useAppSelector(selectHasRequiredAddressForValuation)
  const callToActionDescription = t(
    price !== undefined
      ? 'wizard.screen.basicInfo.price.recommendedNotification.callToActionWithPrice'
      : 'wizard.screen.basicInfo.price.recommendedNotification.callToActionAddressPresent'
  )

  useLoadPriceMaximizerData(insertionData, isRecommendedPriceEnabled)
  useLoadRentValuationData(insertionData, isRecommendedPriceEnabled)

  if (!isRecommendedPriceEnabled) {
    return null
  }

  if (isPending) {
    return (
      <div className='grid-item one-whole grid grid-flex grid-align-center gap-s'>
        <Loader size='xsmall' variant='secondary' />
        <span className='font-body-small-regular'>
          {t('wizard.screen.basicInfo.price.recommendedNotification.loading')}
        </span>
      </div>
    )
  }

  if (!hasRequiredAddressData) {
    return (
      <div className='grid-item one-whole'>
        <MissingRequiredDataNotification />
      </div>
    )
  }

  if (minValue === undefined || maxValue === undefined) {
    return null
  }

  const onOpenDetails = () => {
    const nextVariant: RecommendedPriceNotificationModalVariant =
      price !== undefined && price > maxValue ? 'priceAboveRange' : 'fairPrice'
    setModalVariant(nextVariant)
  }

  return (
    <div className='grid-item one-whole'>
      <MarketRangeCtaNotification description={callToActionDescription} onOpenDetails={onOpenDetails} />
      {modalVariant && (
        <RecommendedPriceNotificationModal
          isOpen={true}
          onClose={() => setModalVariant(null)}
          variant={modalVariant}
          price={price}
          minValue={minValue}
          maxValue={maxValue}
        />
      )}
    </div>
  )
}
